import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Checkbox,
  Input,
  FormLabel,
  Textarea,
  Button,
} from "@chakra-ui/react";
import Select from "react-select";
import { Plus } from "lucide-react";

import {
  dateComponent,
  stringComponent,
  numberComponent,
  stockComponent,
  stringConditions,
  dropdownConditions,
  categoryOptions,
  subCategoryOptions,
} from "../utils/SearchPickList";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { getCollaction } from "../../../redux/reduxApi/admin/CollactionReduxApi";
import { getCategoryList } from "../../../redux/reduxApi/admin/CategoryReduxApi";
import { setFilteredStatus } from "../../../redux/reduxSlice/admin/CategorySlice";
import { toast } from "react-toastify";

export default function SearchCollection({ isSearchDrawerOpen, closeDrawer }) {

  const dispatch = useDispatch();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const filtersConfig = [
    { key: "name", label: "Category Name", optionsType: "string" },
    { key: "parentCategoryId", label: "Parent Category", optionsType: "category" },
  ];

  useEffect(() => {
    dispatch(getCategoryList()).then((res) => {
      const data = res.payload?.data || [];

      const options = data
        .filter(item => item.parentCategoryId === null)
        .map(item => ({
          label: item.name,
          value: item.ROWID
        }));

      setCategoryOptions(options);
    });
  }, [dispatch]);

  // -------------------- STATE --------------------
  const [filters, setFilters] = useState(
    filtersConfig.reduce((acc, f) => {
      acc[f.key] = {
        checked: false,
        condition: { label: "Is", value: "is" },
        showInput: false,
        value: "",
      };
      return acc;
    }, {})
  );

  const handleReset = () => {
    setFilters(
      filtersConfig.reduce((acc, f) => {
        acc[f.key] = {
          checked: false,
          condition: { label: "Is", value: "is" },
          showInput: false,
          value: "",
        };
        return acc;
      }, {})
    );
  };

  // -------------------- HANDLERS --------------------
  const handleCheckboxChange = (key) => (e) => {
    const checked = e.target.checked;
    setFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        checked,
        condition: { label: "Is", value: "is" },
        showInput: checked,
        value: "",
      },
    }));
  };

  const handleSelectChange = (key) => (selected) => {
    const isDropdownField = ["parentCategory", "subCategory"].includes(key);
    const hideInputFor = ["is-empty", "is-not-empty"];
    setFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        condition: selected,
        showInput: !hideInputFor.includes(selected.value) && (isDropdownField
          ? ["is", "is-not", "contains", "not-contains", "starts-with", "ends-with", "like"].includes(selected.value)
          : [
            "is",
            "is-not",
            "contains",
            "not-contains",
            "starts-with",
            "ends-with",
            "like",
          ].includes(selected.value)
        ),
      },
    }));
  };

  const handleValueChange = (key) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const getOptions = (type) => {
    switch (type) {
      case "category":
        return categoryOptions;
      default:
        return [];
    }
  };

  const handleOnClose = () => {
    setFilters(
      filtersConfig.reduce((acc, f) => {
        acc[f.key] = {
          checked: false,
          condition: { label: "Is", value: "is" },
          showInput: false,
          value: "",
        };
        return acc;
      }, {})
    );
    closeDrawer();
  };


  const handleSearch = async () => {
    const search = [];

    filtersConfig.forEach(({ key }) => {
      const current = filters[key];

      if (current.checked) {
        let value = current.value;
        let operation = current.condition?.value || "is";

        search.push({
          field: key,
          operation,
          value: value === "" ? null : value
        });
      }
    });

    if (search.length === 0) {
      toast.warning("Select at least one filter");
      return;
    }

    const query = {
      page: 1,
      limit: 30,
      search,
    };

    console.log("CATEGORY SEARCH:", query);

    sessionStorage.setItem("categorySearchQuery", JSON.stringify(query));

    try {
      const res = await dispatch(getCategoryList(query));

      if (res?.error || res?.payload?.success === false) {
        toast.warning("No records found!");
        dispatch(setFilteredStatus(false));
        return;
      }

      dispatch(setFilteredStatus(true));
      toast.success("Filtered successfully");
      closeDrawer();

    } catch (err) {
      console.error(err);
      toast.error("Search failed");
    }
  };




  return (
    <div>
      <Drawer isOpen={isSearchDrawerOpen} placement="right" size="xs" onClose={handleOnClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <div className="d-flex justify-content-start align-items-center gap-4">
              <span>Search Category</span>

              <button className="reset-btn" onClick={handleReset}> Reset</button>

            </div>


          </DrawerHeader>
          <DrawerBody>
            <form>
              {filtersConfig.map((filter) => {
                const { key, label, optionsType } = filter;
                const current = filters[key];

                return (
                  <div className="mt-3" key={key}>
                    <Checkbox
                      isChecked={current.checked}
                      onChange={handleCheckboxChange(key)}
                    >
                      {label}
                    </Checkbox>

                    {current.checked && (
                      <>
                        <div className="mt-2">
                          <Select
                            options={
                              ["category"].includes(optionsType)
                                ? dropdownConditions
                                : stringConditions
                            }
                            value={current.condition}
                            onChange={handleSelectChange(key)}
                          />
                        </div>

                        {current.showInput && (
                          <>
                            {["category"].includes(optionsType) ? (
                              <Select
                                className="mt-2"
                                isClearable
                                options={getOptions(optionsType)}
                                placeholder={`Select ${label}`}
                                onChange={(opt) =>
                                  handleValueChange(key)(opt ? opt.value : "")
                                }
                                value={
                                  getOptions(optionsType).find(
                                    (o) => o.value === current.value
                                  ) || null
                                }
                              />
                            ) : (
                              <Input
                                className="mt-2"
                                type="text"
                                placeholder={`Enter ${label}...`}
                                value={current.value || ""}
                                onChange={(e) =>
                                  handleValueChange(key)(e.target.value)
                                }
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </form>
          </DrawerBody>
          <center>
            <DrawerFooter style={{ display: "inline-block" }}>
              <div className="gap-3 d-flex">
                <button
                  variant="outline"
                  mr={3}
                  onClick={handleOnClose}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button className="main-btn" onClick={handleSearch}>
                  <span className='inner-content'>    Search</span>
                </button>
              </div>
            </DrawerFooter>
          </center>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
